import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useMap } from '../map/MapContext';
import { getUserData, storeUserData } from '../services/CacheService';

interface Bookmark {
  id: string;
  name: string;
  latitude: number;
  longitude: number;
  notes: string;
  createdAt: number;
  collection: string;
}

const DEFAULT_COLLECTION = 'Default';
const STORAGE_KEY = 'efb-bookmarks';
const METADATA_KEY = 'bookmarks';

function loadBookmarks(): Bookmark[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

function saveBookmarks(bm: Bookmark[]) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(bm)); } catch { /* unavailable */ }
  void storeUserData(METADATA_KEY, bm).catch(() => { /* local fallback remains available */ });
}

export const BookmarksPanel: React.FC = () => {
  const { map } = useMap();
  const [bookmarks, setBookmarks] = useState<Bookmark[]>(loadBookmarks);
  const [name, setName] = useState('');
  const [notes, setNotes] = useState('');
  const [activeCollection, setActiveCollection] = useState(DEFAULT_COLLECTION);
  const idRef = useRef(0);

  useEffect(() => {
    let cancelled = false;
    void getUserData<Bookmark[]>(METADATA_KEY).then((stored) => {
      if (!cancelled && stored) {
        setBookmarks(stored);
        try { localStorage.setItem(STORAGE_KEY, JSON.stringify(stored)); } catch { /* unavailable */ }
      }
    }).catch(() => { /* local fallback remains available */ });
    return () => { cancelled = true; };
  }, []);

  const collections = [...new Set([DEFAULT_COLLECTION, ...bookmarks.map((b) => b.collection)])];

  const addBookmark = useCallback(() => {
    if (!map || !name.trim()) return;
    const center = map.getCenter();
    const bm: Bookmark = {
      id: `bm-${Date.now()}-${idRef.current++}`,
      name: name.trim(),
      latitude: center.lat,
      longitude: center.lng,
      notes: notes.trim(),
      createdAt: Date.now(),
      collection: activeCollection,
    };
    const next = [...bookmarks, bm];
    setBookmarks(next);
    saveBookmarks(next);
    setName('');
    setNotes('');
  }, [map, name, notes, activeCollection, bookmarks]);

  const removeBookmark = useCallback((id: string) => {
    const next = bookmarks.filter((b) => b.id !== id);
    setBookmarks(next);
    saveBookmarks(next);
  }, [bookmarks]);

  const flyTo = useCallback((bm: Bookmark) => {
    map?.flyTo({ center: [bm.longitude, bm.latitude], zoom: 12, duration: 1200 });
  }, [map]);

  const exportGeoJSON = useCallback(() => {
    const fc = {
      type: 'FeatureCollection',
      features: bookmarks.filter((b) => b.collection === activeCollection).map((b) => ({
        type: 'Feature',
        properties: { name: b.name, notes: b.notes, collection: b.collection },
        geometry: { type: 'Point', coordinates: [b.longitude, b.latitude] },
      })),
    };
    const blob = new Blob([JSON.stringify(fc, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `bookmarks-${activeCollection}.geojson`; a.click();
    URL.revokeObjectURL(url);
  }, [bookmarks, activeCollection]);

  const filtered = bookmarks.filter((b) => b.collection === activeCollection);

  return (
    <div>
      {/* Collection chips */}
      <div className="panel-section">
        <div className="panel-section-title">Collections</div>
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {collections.map((c) => (
            <button
              key={c}
              className={`chip ${c === activeCollection ? 'active' : ''}`}
              onClick={() => setActiveCollection(c)}
            >
              {c} ({bookmarks.filter((b) => b.collection === c).length})
            </button>
          ))}
        </div>
      </div>

      {/* Add bookmark */}
      <div className="panel-section">
        <div className="panel-section-title">Add Bookmark at Map Center</div>
        <input className="input" placeholder="Name" value={name} onChange={(e) => setName(e.target.value)} style={{ marginBottom: 4 }} />
        <input className="input" placeholder="Notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} style={{ marginBottom: 6 }} />
        <button className="btn btn-primary" onClick={addBookmark} disabled={!name.trim()} style={{ width: '100%' }}>
          📌 Save Bookmark
        </button>
      </div>

      {/* Bookmark list */}
      <div className="panel-section">
        <div className="panel-section-title">{filtered.length} Bookmarks</div>
        {filtered.map((bm) => (
          <div key={bm.id} className="list-item">
            <div style={{ flex: 1, cursor: 'pointer' }} onClick={() => flyTo(bm)}>
              <div style={{ fontWeight: 600, fontSize: 13 }}>{bm.name}</div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                {bm.latitude.toFixed(4)}°, {bm.longitude.toFixed(4)}°
                {bm.notes ? ` — ${bm.notes}` : ''}
              </div>
            </div>
            <button className="btn-icon" onClick={() => removeBookmark(bm.id)} title="Delete">🗑️</button>
          </div>
        ))}
      </div>

      {/* Export */}
      <div className="panel-section">
        <div style={{ display: 'flex', gap: 6 }}>
          <button className="btn btn-secondary" onClick={exportGeoJSON}>📄 Export GeoJSON</button>
        </div>
      </div>
    </div>
  );
};
